'use client';

import type React from 'react';
import { useMemo } from 'react';

import VideoAwareAutoHyperlink from '@/components/video/VideoAwareAutoHyperlink';

import { AtLeastOneOf } from '@/types/Utility.types';

import { CachedContentData } from '@/hooks/useCachedContentData';

import ExpandableText from '@cineamo/ui/src/misc/ExpandableText';

import MobileOrDesktopRenderer from '@cineamo/composables/src/MobileOrDesktopRenderer';
import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

import { CustomContentMovie } from '@/utils/custom-content';

// one of these props must be provided
type RequiredProps = {
    content: CachedContentData;
    customContentMovie: CustomContentMovie;
};

type OneOfRequiredProps = AtLeastOneOf<RequiredProps, keyof RequiredProps>;

const MovieDescription: React.FC<
    OneOfRequiredProps & {
        maxLinesMobile?: number;
        maxLinesDesktop?: number;
    }
> = ({ content, customContentMovie, maxLinesMobile = 5, maxLinesDesktop = 3 }) => {
    const { track } = useAnalytics();

    const overview = useMemo(() => {
        return customContentMovie?.description || content?.description;
    }, [customContentMovie, content]);

    const overviewWithLinks = useMemo(() => {
        return (
            <VideoAwareAutoHyperlink trackingEvent="Movie Description Video Played">{overview}</VideoAwareAutoHyperlink>
        );
    }, [overview]);

    function onExpandOverview() {
        track('Movie Description Text Expanded', {
            movieId: content?.movieId ?? ''
        });
    }
    return (
        <>
            {overview && (
                <MobileOrDesktopRenderer
                    renderBeforeMount={maxLinesMobile ? 'mobile' : 'desktop'}
                    mobileContent={
                        <ExpandableText
                            className="pointer-events-auto"
                            maxLines={maxLinesMobile}
                            text={overviewWithLinks}
                            onClick={onExpandOverview}
                            initiallyExpanded={content?.hasCustomContent}
                        />
                    }
                    desktopContent={
                        <ExpandableText
                            className="pointer-events-auto"
                            maxLines={maxLinesDesktop}
                            text={overviewWithLinks}
                            onClick={onExpandOverview}
                            initiallyExpanded={content?.hasCustomContent}
                        />
                    }
                />
            )}
        </>
    );
};

export default MovieDescription;
